import React, { useState } from 'react';
import { useTeam } from '../context/TeamContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Users, CheckCircle2, Clock } from 'lucide-react';

/**
 * TeamGate Component
 * 
 * Wraps protected content and enforces team membership.
 * Shows different UI based on user's team status:
 * - none: "Join Team" form
 * - pending: "Request pending" message
 * - approved: Renders children (actual content)
 */
const TeamGate = ({ children, title = "Team Access Required" }) => {
    const { teamStatus, team, loading, joinTeam, refreshTeamStatus } = useTeam();
    const [teamCode, setTeamCode] = useState('');
    const [joining, setJoining] = useState(false);
    const [joinError, setJoinError] = useState(null);
    const [joinSuccess, setJoinSuccess] = useState(false);

    const handleJoinTeam = async (e) => {
        e.preventDefault();

        if (!teamCode.trim()) {
            setJoinError('Please enter a team code');
            return;
        }

        setJoining(true);
        setJoinError(null);
        setJoinSuccess(false);

        const result = await joinTeam(teamCode.trim());

        if (result.success) {
            setJoinSuccess(true);
            setTeamCode('');
            // Refresh to get updated status
            setTimeout(() => refreshTeamStatus(), 1000);
        } else {
            // Handle specific error codes
            if (result.errorCode === 'INVALID_TEAM_CODE') {
                setJoinError('Invalid team code. Please check and try again.');
            } else if (result.errorCode === 'ALREADY_MEMBER') {
                setJoinError('You are already a member of this team.');
            } else if (result.errorCode === 'REQUEST_ALREADY_PENDING') {
                setJoinError('You already have a pending request for this team.');
            } else {
                setJoinError(result.error);
            }
        }

        setJoining(false);
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                    <p className="text-muted-foreground">Loading team information...</p>
                </div>
            </div>
        );
    }

    // User has approved team membership - show content
    if (teamStatus === 'approved') {
        return <>{children}</>;
    }

    // User has pending request
    if (teamStatus === 'pending') {
        return (
            <div className="flex items-center justify-center min-h-[400px] p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900">
                            <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <CardTitle>Request Pending</CardTitle>
                        <CardDescription>
                            Your request to join <strong>{team?.name}</strong> is awaiting approval
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Alert>
                            <AlertDescription>
                                A coach will review your request soon. You'll be able to access team content once approved.
                            </AlertDescription>
                        </Alert>
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={refreshTeamStatus}
                        >
                            Refresh Status
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // User has no team - show join form
    return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                        <Users className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>
                        Join a team to access this content. Enter your team code below.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleJoinTeam} className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="teamCode" className="text-sm font-medium">
                                Team Code
                            </label>
                            <Input
                                id="teamCode"
                                type="text"
                                placeholder="Enter team code"
                                value={teamCode}
                                onChange={(e) => setTeamCode(e.target.value.toUpperCase())}
                                disabled={joining}
                                className="uppercase"
                            />
                        </div>

                        {joinError && (
                            <Alert variant="destructive">
                                <AlertDescription>{joinError}</AlertDescription>
                            </Alert>
                        )}

                        {joinSuccess && (
                            <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                                <AlertDescription className="text-green-800 dark:text-green-200">
                                    Request submitted successfully! Waiting for coach approval.
                                </AlertDescription>
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={joining || !teamCode.trim()}
                        >
                            {joining ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Joining...
                                </>
                            ) : (
                                'Join Team'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center text-sm text-muted-foreground">
                        <p>Don't have a team code?</p>
                        <p className="mt-1">Ask your coach or team administrator.</p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default TeamGate;
